import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch, useHistory, useLocation } from "react-router-dom";

import {
  switchBreakpoint,
  undo,
  redo,
  updateToolsSettings,
  switchInteractivePageSection,
  recalculateLayout,
  updateSpatialSettingMode,
  selectPageSegment,
  actionGroup,
  aiMobileOptimizationRequest,
  applyWidgetPropertyChanges,
} from "actions/editor";

import {
  publishPage,
  searchPages,
  revalidatePagesList,
  createPublishRequest,
  updatePublishRequest,
  invalidatePublishRequset,
  invalidatePageChange,
} from "actions/pages";

import {
  fetchComponentsForPalette,
  fetchPalettes, fetchPalettesForWorkspace
} from 'actions/palette';

import { fetchGatings, publishGating, updateProgressiveGatingProperty } from "actions/gating";
import { fetchAllPagesForInteractiveStoryboard, fetchStoryboard } from 'actions/storyboard';

import { 
  desktopToMobileOptimize, mobileLayoutOptimize, desktopToMobilePlanAndOptimize
} from "actions/mobileOptimization";

import {
  Offcanvas,
  Tab,
  Button,
  TabContainer,
  Dropdown,
  Row,
  Col,
  Image,
  Modal,
  Form,
  OverlayTrigger,
  Popover
} from "react-bootstrap";

import _ from 'lodash';
import cx from 'classnames';
import Truncate from "react-truncate";
import dateformat from "dateformat";
import { useIntercom } from 'react-use-intercom';
import { toast } from "react-toastify";
import { FailureToast, SuccessToast } from "components/Toast";

import * as widgetTypes from "constants/widgetTypes";

import { useInteractiveLiveUrl } from 'components/Editor/hooks/useEditorSession';
import Tooltip from "components/Tooltip";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { Palette, GatingPalette, LiveElementPalette } from "components/Palette";
import { CreateVersionModal, NewVersionModal, PublishLoader } from "./../../components";
import PageSettings from "./../../pageSettings";
import IcV2PageSettings from "../../pageSettings/IcV2PageSettings";
import { GatingSettingsPage } from 'pages/pages/gating/settings';
import { formFieldTypes } from "components/Editor/widgets/form";
import LangingPageSettings from 'pages/pages/landing/settings';
import { AssetManagerModal } from "components/AssetManager";
import PageHealth from "components/PageHealth";
import { ProgressiveGatingSetup } from "../progressiveGating";
import moment from "moment";
import storyboardRoleService from 'services/storyboardRoles.service';
import { CONTENT_CARD_TEMPLATE } from "constants/widgetTypes";
import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_MOBILE_OPTIMIZATION_CLICKED, MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_EDITOR } from "constants/mixpanelEvents";
import { getPageViewedEventPayload, getStoryboardMixpanelEventPayload } from "services/mixpanelUtil";
import { getBaseUrl } from 'services/util';
import { generateHTML } from "components/Editor/renderer/generator";
import { generateLayoutChanges } from "components/Editor/renderer/layoutGenerator";

import { CreateStoryboard } from "pages/storyboards/templates";
import smartGatingIcon from "../../../../assets/images/smartGatingBorder.svg";

import { STORYBOARD_TYPES, MULTIPAGE_STORYBOARDS, INTERACTIVE_CONTENT_TYPES, SEGMENT_STORYBOARDS } from "constants/storyboardType";
import { isIcV2 } from "pages/pages/preview/icV2/helper";
import styles from './header.module.scss';

const INTERCOM_ID = process.env.REACT_APP_CLEVERSTORY_INTERCOM_ID;
const MAX_WIDGET_COUNT = 1500;
const WIDGET_RANGE_GUIDE = {
  good: [0, 800],
  fair: [800, 1000],
  poor: [1000, Infinity],
};

export const HeaderContext = React.createContext({
  type: '',
  entity: {},
  editorSession: null
})

export const PageHeader = ({ disabled, updateOptimzeLayout, isOptimizing}) => {
  const dispatch = useDispatch();
  const [selectedDomain, setSelectedDomain] = useState({});
  const { boot, show } = useIntercom();

  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', null));
  const superAdmin = _.get(workspace, 'superAdmin', false);
  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain') || "");
  const workspaces = useSelector((state) => _.get(state, "workspaces.items", null));

  const headerContext = React.useContext(HeaderContext);

  const id = _.get(headerContext, 'id', '');
  const [showContentManager, setShowContentManager] = useState(false);
  const entity = _.get(headerContext, 'entity', null);
  const editorSession = _.get(headerContext, 'editorSession', null);
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));

  const type = _.get(headerContext, 'type', 'page');

  const updateState = _.get(headerContext, 'updateState', {});

  const autoSaveState = _.get(headerContext, 'autoSaveState', {});
  const updating = _.get(updateState, 'updating') || _.get(autoSaveState, 'updating');

  const storyboard = useSelector(state => _.get(state, 'storyboards.storyboard.item', {}));
  const storyboardType = _.get(storyboard, 'type', '');
  const passwordProtected = _.get(storyboard, 'passwordProtected');
  const pages = useSelector(state => _.get(state, 'pages.items', []));
  const gatings = useSelector(state => _.get(state, 'gatings.items', []));
  const interactivePages = useSelector(state => _.get(state, 'storyboards.storyboard.pages.items', []));

  const publishRequest = useSelector((state) => _.get(state, `pages.publish`, {}));
  const publishItems = useSelector((state) => _.get(state, `pages.publish.items`, []));
  const publishing = useSelector((state) => _.get(state, `pages.publish.publishing`, false));
  const pageItems = useSelector((state) => _.get(state, 'pages.page', []));
  const allVariableMappings = publishItems.flatMap((item) => {
    const page = _.get(pageItems, `${item.id}.item`, {});
    const widgets = _.get(page, 'draft.widgets', []);
    const textWidgets = widgets.filter((w) => w.type === widgetTypes.TEXT);
    const variables = textWidgets.flatMap((w) => w.properties?.desktop?.variables || []);
    const variableMappings = _.get(page, "variableMappings", []);

    if (Array.isArray(variableMappings) && variableMappings.length > 0) {
      return variableMappings;
    }

    if (Array.isArray(variables) && variables.length > 0) {
      return variables;
    }

    return [];
  });

  const hasVariableMappings = allVariableMappings.length > 0; 

  const restoreState = useSelector((state) => _.get(state, `pages.page.${id}.restore`));
  const restoring = _.get(restoreState, 'restoring');
  const user = useSelector((state) => _.get(state, "users.me"));

  var liveAlias = useInteractiveLiveUrl(editorSession, null);
  var liveUrl = _.get(storyboard, 'page.canonical');

  const creatingTemplate = useSelector(state => _.get(state, 'templates.creating',false));
  const updatingTemplate = useSelector(state => _.get(state, 'templates.updating',false));

  const present = useSelector(state => _.get(state, `${editorSession}.present`));
  const activePage = present?.widgets?.find(item => item.uid === _.get(present, `interactive.active`, ""));
  const hiding = useSelector(state => _.get(state, `pages.pageHiding`, false));
  const activePageAlias = _.get(activePage, 'properties.desktop.alias', '');

  const [showVersionModal, setShowVersionModal] = useState(false);
  const closeVersionModal = () => setShowVersionModal(false);

  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const closeTemplate = () => setShowCreateTemplate(false);
  const icV2pages = useSelector((state) => _.get(state, "pages.icV2.pages", []));

  useEffect(() => {
    if (storyboard && storyboard.id) {
      dispatch(fetchGatings({
        storyboard: storyboard.id,
        published: false
      }, 0, 50));
    }

    let domainUrl = _.get(storyboard,'domain') || domain;
    setSelectedDomain(domainUrl);
  }, [storyboard]);

  const handlePageSave = () => {
    headerContext.onSave();
  };

  const handlePublishRequest = (data, num, storyboardType) => {
    const itemsToPublish = data.slice(0, num);
    const totalWaitingItems = data.length;
    
    itemsToPublish.forEach((item, index) => {
      const isLast = (index === itemsToPublish.length - 1) && (totalWaitingItems === itemsToPublish.length) && storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 && item.type === 'page';
      const payload = {
        id: item.id,
        ...(isLast && item.type === 'page' && { isLast })
      }
      if (item.type === 'page') {
        dispatch(publishPage(payload));
      } else {
        dispatch(publishGating(item.id));
      }
    });
  }

  const initializeIntercom = () => {

    boot({
      appID: INTERCOM_ID,
      name: `${user.firstName} || '' ${user.lastName} || ''`,
      userId: `${user.email}`,
      email: `${user.email}`,
      hideDefaultLauncher: true,
    })
  }

  useEffect(() => {
    if (user) {
      initializeIntercom();
    }
  }, [user]);

  useEffect(() => {
   if (!publishRequest || !publishRequest.publishing || publishRequest.published) {
      return;
    }
    var waitingPages = publishItems.filter((data) => !data.published && !data.publishing);
    var publishingPages = publishItems.filter((data) => data.publishing);
    if (publishingPages.length === 0 && waitingPages.length > 0) {
      handlePublishRequest(waitingPages, Math.min(waitingPages.length, 4, storyboardType));
    }
    if (publishingPages.length < 4 && waitingPages.length > 0) {
      handlePublishRequest(waitingPages, 1, storyboardType);
    }
    if (waitingPages.length === 0) {
      dispatch(updatePublishRequest({
        published: true
      }));
    }
  }, [publishRequest]);


  const handlePagePublish = () => {
    if (!canPublish) {
      return;
    }

    var pageRequest = [];

    if (storyboardType === 'INTERACTIVE_CONTENT') {
      const pageId = interactivePages[0].page;
      pageRequest = [{
        id: pageId,
        name: storyboard.name,
        published: false,
        publishing: false,
        error: false,
        type: 'page'
      }]
    }

    if (storyboardType !== 'INTERACTIVE_CONTENT') {
      pageRequest = pages.filter((data) => data.changes === true).map(item => {
        return {
          id: item.id,
          name: item.name,
          published: false,
          publishing: false,
          error: false,
          type: 'page'
        }
      })
    }

    var gatingRequest = gatings.filter(item => item.changes === true).map(item => {
      return {
        id: item.id,
        name: item.name,
        published: false,
        publishing: false,
        error: false,
        type: 'gating'
      }
    })

    var request = [...pageRequest, ...gatingRequest];

    dispatch(createPublishRequest(request))
  }

  const handlePageRestore = () => {
    headerContext.onRestore();
  }

  const handlePagePreview = () => {
    if (type !== 'page' && storyboardType !== 'INTERACTIVE_CONTENT') {
      return;
    }

    var pageId = entity.id;

    if (storyboardType === 'INTERACTIVE_CONTENT' && type === 'gating') {
      pageId = interactivePages[0].page;
    }

    if (isIcV2(storyboardType)) {
      pageId = _.find(icV2pages, { hidden: false }).id ?? pageId;
    }

    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/pages/${pageId}/preview`;
    window.open(url);
  };

  const handleViewPage = () => {
    var alias = _.get(entity, 'canonical', '');

    if (storyboardType === 'INTERACTIVE_CONTENT') {
      alias = activePageAlias;
    }

    const url = `${selectedDomain.url}${alias}`;
    window.open(url);
  }

  const handleVersionPreview = () => {
    if (type !== 'page' && storyboardType === 'LANDING_PAGE') {
      return;
    }

    var pageId = entity.id;

    if (storyboardType === 'INTERACTIVE_CONTENT' && type === 'gating') {
      pageId = interactivePages[0].page;
    }

    const url = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/pages/${pageId}/versions/current`;
    window.open(url);
  }

  const handleStoryboardSettingsClick = () => {
    const storyboardUrl = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/storyboards/${storyboard.id}`;

    window.open(`${storyboardUrl}/website-settings`);
  }

  const handleCreateTemplateClick = () => {
    setShowCreateTemplate(true);
  }

  const handleBackToStoryBoard = () => {
    const storyboardUrl = `${process.env.REACT_APP_UI_URL}/app/${workspace.id}/storyboards`;
    window.open(`${storyboardUrl}`);
  }

  const canPublish = () => {
    if (!storyboardRoleService.canManageStoryboard(storyboard)) {
      return false;
    }

    if (publishing || updating) {
      return false;
    }

    if (storyboardType !== 'INTERACTIVE_CONTENT' && pages && !canPublishLandingPage() && !canPublishGatings()) {
      return false
    }

    return true;
  }

  const canPublishLandingPage = () => {
    var changedPages = pages.filter((data) => data.changes);
    return changedPages.length > 0;
  }

  const canPublishGatings = () => {
    var changes = gatings.filter(item => item.changes);
    return changes.length > 0;
  }

  const renderTitle = () => {
    if (workspaces.length > 1) {
      return `${workspace.name} / Storyboard:`
    }

    return "Storyboard:"
  }

  const handleToggleContentManager = () => {
    setShowContentManager(!showContentManager)
  }

  return (
    <>
      <header className={styles.header}>
        <div className={cx(styles.menu, styles.primary)}>
          <div className={styles.logoContainer}>
            <div className={styles.logo}></div>
          </div>
          <Dropdown className={styles.filter}>
            <span className={styles.storyboardHeader}>{renderTitle()}</span>
            <Dropdown.Toggle>
              <TruncatedTextWithTooltip placement="bottom" className={styles.name} content={_.get(storyboard, "name")}>
                {_.get(storyboard, "name")}
              </TruncatedTextWithTooltip>
            </Dropdown.Toggle>
            <Dropdown.Menu className={styles.filterMenu}>
              {type === 'page' && _.get(entity, "published") && 
                <> 
                  <Dropdown.Item className={styles.publishDate} disabled>Last Published: {dateformat(entity.lastPublished, 'dd mmm yyyy')}</Dropdown.Item>
                  <Dropdown.Divider />
                </>
              }

              <Dropdown.Item disabled={type !== 'page' || !_.get(entity, "published")} onClick={handleViewPage}>View Last Published</Dropdown.Item>
              
              {(!disabled &&  storyboardType !== 'SPATIAL' && entityType !== 'GATING') &&
                <>
                  <Dropdown.Item onClick={()=>{setShowVersionModal(true)}}>Save as a New Version</Dropdown.Item>
                  {type === 'page' && <Dropdown.Item onClick={()=> handleVersionPreview()}>Show Version History</Dropdown.Item>}  
                </>
              }
              
              <Dropdown.Item active={false} onClick={handleStoryboardSettingsClick}>Storyboard Settings</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleBackToStoryBoard}><i className={cx("cleverstory-left", styles.backIcon)} /> My Storyboard</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {superAdmin && entityType === 'PAGE' &&
            <div className={styles.templateContainer}>
              <Button
                variant="light"
                className={cx(styles.templateButton, styles.actionButton)}
                disabled={creatingTemplate || updatingTemplate || !_.get(entity, "published")}
                onClick={handleCreateTemplateClick}
              >
                {creatingTemplate ? 'Saving Template...' : updatingTemplate ? 'Updating Template...' : 'Save as Template'}
              </Button>  
            </div>
          }
          
          <div className={cx(styles.actions, styles.right)}>
            {!updating && entity &&
              <div className={styles.lastSavedDate}>Last Saved: <LastUpdatedDate value={entity.modifiedDate} /></div>
            }
            {storyboardRoleService.canEditStoryboard(storyboard) &&
              <Button
                variant="light"
                className={cx(styles.saveButton, styles.actionButton)}
                disabled={disabled || updating || hiding}
                onClick={handlePageSave}
              >
                {updating ? 'Saving...' : 'Save Changes'}
              </Button>
            }
            <Tooltip placement='bottom' content='Preview Page'>
              <Button variant="info" disabled={type !== 'page' && storyboardType !== 'INTERACTIVE_CONTENT'} className={styles.actionButton} onClick={handlePagePreview}>
                <i className={cx("cleverstory-view")} />
              </Button>
            </Tooltip>
            <Button
              disabled={disabled || !canPublish()}
              variant="primary"
              onClick={handlePagePublish}>
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
            <div className={styles.helpButtonContainer}>
              <Button size='sm' variant='primary' onClick={show} className={styles.helpButton}>
                <i className={cx(styles.helpIcon, "cleverstory-support-help")} />
                Help
              </Button>
            </div>
          </div>
        </div>
        <SecondaryHeader 
          editorSession={editorSession} 
          openContentManager={handleToggleContentManager} 
          page={entity} 
          storyboard={storyboard} 
          disabled={disabled}
          entityType={entityType}
          updateOptimzeLayout={updateOptimzeLayout}
          isOptimizing={isOptimizing}
        />
      </header>
      <PublishLoader
        editorSession={editorSession}
        show={publishing}
        passwordProtected={passwordProtected}
        totalPages={publishItems}
        pageId={_.get(storyboard, 'page.id')}
        domain={_.get(selectedDomain, 'url')}
        siteUrl={`${_.get(selectedDomain, 'url')}${storyboardType === 'INTERACTIVE_CONTENT' ? (activePageAlias || liveUrl) : liveUrl}`}
        percentage={publishRequest.percentage || 0}
        hasVariableMappings={hasVariableMappings}
        onDiscard={() => {
          dispatch(invalidatePublishRequset())
        }}
      />

      <CreateVersionModal
        show={showVersionModal}
        onDiscard={closeVersionModal}
        storyboard={storyboard}
      />
      
      <AssetManagerModal
        multiple={true}
        show={showContentManager}
        edit={true}
        onClose={handleToggleContentManager}
        editorSession={editorSession}
      />

    {superAdmin &&
      <CreateStoryboard show={showCreateTemplate} onClose={closeTemplate} create={true}></CreateStoryboard>
    }
    </>
  );
}

const SecondaryHeader = ({ editorSession, page, storyboard, openContentManager, disabled, entityType, updateOptimzeLayout, isOptimizing }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);
  const { id } = useParams();
  const aiOptimizationRef = React.useRef(null);
  const optimizeLockRef = React.useRef(false);
  const [popoverWidth, setPopoverWidth] = useState(null);

  const headerContext = React.useContext(HeaderContext);
  const type = _.get(headerContext, 'type', 'page');

  const me = useSelector((state) => _.get(state, "users.me"));
  const aiMobileOptimizationEnabled = useSelector(state => _.get(state, 'workspaces.workspace.item.aiMobileOptimizationEnabled', false));
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`, []));
  const mobileOptimizationPageWidgets = useSelector(state => _.get(state, `${editorSession}.present.mobileOptimization.pageWidgets`, []));
  const pageWidgets = React.useMemo(() => {
    return widgets.filter(item => item.type === widgetTypes.PAGE);
  }, [widgets]);
  let templateWidgetUids = [];
  const filteredWidgets = widgets.filter((item) => {
    if (item.type === CONTENT_CARD_TEMPLATE) {
      templateWidgetUids.push(item.uid);
      return false; 
    }
    if (templateWidgetUids.includes(item.parent)) {
      return false;
    }
    return true; 
  });
  const layoutChangeIndicator = useSelector(state => _.get(state, `${editorSession}.present.layoutIndicator`));

  const [state, setState] = useState({
    selected: null,
  });

  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );

  const canUndo = useSelector(
    (state) => _.size(_.get(state, `${editorSession}.past`)) > 0
  );

  const canRedo = useSelector(
    (state) => _.size(_.get(state, `${editorSession}.future`)) > 0
  );

  const tools = useSelector((state) =>
    _.get(state, `${editorSession}.present.tools`)
  )

  const activePage = useSelector(
    (state) => _.get(state, `${editorSession}.present.interactive.active`)
  );

  const allowVariable = useSelector((state) =>  
      _.get(state, `${editorSession}.present.entityType`)) === 'PAGE'; 

  const storyboardId = _.get(storyboard, 'id', '');
  const storyboardName = _.get(storyboard, 'name', '');
  const storyboardType = _.get(storyboard, 'type', '');
  const isMobile = breakpoint === "mobile";
  const isValidStoryboard = !['SPATIAL'].includes(storyboardType);
  const isInteractiveContent = storyboardType === 'INTERACTIVE_CONTENT';
  const canUseAIOptimization = isMobile && aiMobileOptimizationEnabled && entityType === "PAGE" && isValidStoryboard;
  const showFallbackOptimizeButton = isMobile && !disabled && !canUseAIOptimization;

  const hasError = useSelector(state => {
    const targetWidgets = isInteractiveContent
      ? [activePage]
      : pageWidgets.map(w => w.uid);
  
    return targetWidgets.some(uid =>
      Boolean(
        _.get(
          state,
          `${editorSession}.present.mobileOptimization.pageWidgets.${uid}.error.message`
        )
      )
    );
  });

  useEffect(() => {
    window.addEventListener('message', handlePageRestore);

    return () => {
      window.removeEventListener('message', handlePageRestore);
    };
  }, []);

  useEffect(() => {
    if (!isOptimizing) {
      optimizeLockRef.current = false;
    }
  }, [isOptimizing]);

  useEffect(() => {
    if (hasError) {
      let errorDescription = "Something went wrong with optimization";
  
      toast(
        <FailureToast
            heading={`Failure`}
            description={errorDescription}
        />,
        {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
        }
      );
    }
  }, [hasError]);

  const handleUndo = () => {
    dispatch(undo(editorSession));
  };

  const handleRedo = () => {
    dispatch(redo(editorSession));
  };

  const handleSwitchBreakpoint = (breakpoint) => {
    dispatch(switchBreakpoint(editorSession, breakpoint));

    var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_EDITOR, storyboardId, storyboardName, storyboardType)
      mixPanelPayload = {
        ...mixPanelPayload,
        editor_type: capitalizeFirstLetter(type),
        device_type: capitalizeFirstLetter(breakpoint)
      }
      dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
  };

  const capitalizeFirstLetter = (value) => {
    if (value) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
  }

  const handleButtonSelection = (active) => {
    setState((state) => {
      return {
        ...state,
        selected: active,
      };
    });
  };

  const handleCloseSlider = () => {
    setState((state) => {
      return {
        ...state,
        selected: null,
      };
    });
  };

  useEffect(() => {
    handleCloseSlider();
  }, [id]);

  const toggleOption = (option) => {
    const value = _.get(tools, option, false);

    var request = {
      [option]: !value
    };

    dispatch(updateToolsSettings(editorSession, request));
  }

  const handleAIOptimization = () => {
    if (optimizeLockRef.current || isOptimizing) return;
    
    optimizeLockRef.current = true;

    const targetWidgets = isInteractiveContent
    ? widgets.filter(w => w.uid === activePage)
    : pageWidgets;

    targetWidgets.forEach((item) => {
      const isAlreadyOptimized =  _.get( mobileOptimizationPageWidgets, `${item.uid}.desktopToMobileOptimized`, false );

      if (isAlreadyOptimized){
        dispatch(mobileLayoutOptimize(storyboardId, item.id, editorSession, item));
      }
      else{
        dispatch(desktopToMobileOptimize(breakpoint, storyboardId, item.id, editorSession, item));
      }
    });
  };

  const handleOptimizeLayout = () => {
    {const mixPanelPayload = getStoryboardMixpanelEventPayload(storyboard.id, storyboard.name, storyboard.type)
      dispatch(createMixpanelEvent(MIXPANEL_MOBILE_OPTIMIZATION_CLICKED, mixPanelPayload));  
    }

    if(SEGMENT_STORYBOARDS.includes(storyboardType)){
      const pages = widgets.filter(page => {
        return page.type === widgetTypes.PAGE || page.type === widgetTypes.MODAL;
      }).map(page => page.uid);

      dispatch(recalculateLayout(editorSession, pages));
      updateOptimzeLayout?.();
      return;
    }

    dispatch(recalculateLayout(editorSession, [activePage]));
    updateOptimzeLayout?.();
  }

  const handlePageRestore = (e) => {
    if(e.data.type === 'restorePage'){
      const homepage = e.data.payload;
      const homepageId = _.get(homepage, 'id')

      if(homepageId){
        history.push(`${baseUrl}/pages/${homepageId}`);
        window.location.reload();
      }
    }
  };

  const handleSpatialBackground = (view) => {
    if (!view) {
      return;
    }
    handleButtonSelection.bind(this, 'page-details')
    dispatch(updateSpatialSettingMode(editorSession, view));
  };

  const popover = (
    <Popover
      id="ai-optimize-popover"
      className={styles.aiOptimizePopup}
      style={{ width: popoverWidth }}
    >
      <Popover.Body>
        <button className={cx(styles.action)} 
        onClick={() => {
          handleOptimizeLayout();
          document.body.click();
        }}>
          <span>Manual Optimize</span>
        </button>
        <button className={cx(styles.action)} 
          onClick={() => {
          handleAIOptimization();
          document.body.click();
        }}>
          <div className={styles.aiIconSection}>
            <Image className={styles.pulseImage} src={smartGatingIcon} width={20} height={20} />
            <Image className={styles.pulseImage2} src={smartGatingIcon} width={20} height={20} />
          </div>
          <span>AI Optimize</span>
        </button>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <div className={cx(styles.menu, styles.secondary)}>
        <div className={cx(styles.actions, styles.paletteActions)}>
          <PaletteButton onButtonSelection={handleButtonSelection} selected={state.selected} disabled={disabled} />
          {storyboardRoleService.canEditStoryboard(_.get(page, 'storyboard', {})) &&
            <PageSettingsButton onButtonSelection={handleButtonSelection.bind(this, 'settings')} disabled={disabled} />
          }
        </div>
        <div className={styles.resourceActions}>
          {storyboardRoleService.canEditStoryboard(_.get(page, 'storyboard', {})) &&
            <GatingSettingsButton onButtonSelection={handleButtonSelection} disabled={disabled} storyboardType={storyboardType} />
          }
        </div>
          <div className={styles.resourceActions}>
            <ResourceCenterButton openContentManager={openContentManager} disabled={disabled}/>
          </div>
        { storyboardType === 'SPATIAL' &&
          <div className={styles.resourceActions}>
            <SlidePageSettingAction disabled={disabled} handleSpatialBackground={handleSpatialBackground} />
          </div>
        }
        <div className={styles.actions}>
          <ToolsButton tools={tools} onToggle={toggleOption} disabled={disabled} />
          <LayersButton tools={tools} onToggle={toggleOption} disabled={disabled} />
          {allowVariable && 
            <VariablesButton tools={tools} onToggle={toggleOption} disabled={disabled} />
          }
        </div>
        <div className={styles.actions}>
          <UndoButton canUndo={canUndo} onUndo={handleUndo} disabled={disabled} />
          <RedoButton canRedo={canRedo} onRedo={handleRedo} disabled={disabled} />
        </div>
        {  storyboardType !== 'SPATIAL' &&     
          <div className={styles.home}>
            {storyboardType === 'INTERACTIVE_CONTENT' &&
              <InteractiveDropdown editorSession={editorSession} storyboard={storyboard} entityType={entityType}/>
            }

            {storyboardType !== 'INTERACTIVE_CONTENT' &&
              <LandingPageDropdown storyboard={storyboard} />
            }
          </div>
        }
        {(entityType !== 'GATING' && !isIcV2(storyboardType)) && 
          <PageHealth
            page={page}
            breakpoint={breakpoint}
            storyboardType={storyboardType} 
            editorSession={editorSession} 
            pageId={id}
            handlePageSettings={() => {
              handleButtonSelection("settings");
            }}
          />
        }
        {entityType === 'GATING' &&
          <ProgressiveGatingTrigger
            id={id}
            editorSession={editorSession}
            isProgressiveGatingEnabled={state.selected === 'progressive-gating'}
            setState={setState}
          />
        }
        {isIcV2(storyboardType) && entityType !== 'GATING' && <div style={{ flex: 1 }}></div>}
        <div className={cx(styles.actions, styles.right, styles.breakpoint, { [styles.editor] : entityType !== 'GATING', [styles.borderLeft] : isIcV2(storyboardType) && entityType !== 'GATING'})}>
          {canUseAIOptimization && (
            <OverlayTrigger
            trigger="click"
            placement="bottom-end"
            rootClose={true}
            overlay={popover}
            >
              <Button
                ref={aiOptimizationRef}
                onClick={() => {
                  const width = aiOptimizationRef.current?.offsetWidth;
                  setPopoverWidth(width ? `${width}px` : "146px");
                }}
                className={cx("small", styles.aiOptimize)}
                variant="primary"
                disabled={isOptimizing || disabled}
              >
                {isOptimizing ? "Optimizing..." : "Optimize Layout"}
              </Button>
            </OverlayTrigger>
          )}
          {showFallbackOptimizeButton &&
            <OptimizeLayoutButton onOptimizeLayout={handleOptimizeLayout} />
          }
          <div className={styles.group}>
            <DesktopButton breakpoint={breakpoint} onSwitchBreakpoint={handleSwitchBreakpoint} />
            <MobileButton breakpoint={breakpoint} layoutChangeIndicator={layoutChangeIndicator} onSwitchBreakpoint={handleSwitchBreakpoint} />
          </div>
        </div>
        <div className={cx(styles.actions, styles.tools)} >
          <RulerButton tools={tools} onToggle={toggleOption} disabled={disabled} />
          <GridlinesButton tools={tools} onToggle={toggleOption} disabled={disabled} />
        </div>
      </div>
      <PageAction
        editorSession={editorSession}
        selected={state.selected}
        onClose={handleCloseSlider}
        storyboard={storyboard}
        disabled={disabled}
        entityType={entityType}
      />
      {editorSession &&
        <ProgressiveGatingSetup
          selected={state.selected}
          onClose={handleCloseSlider}
          editorSession={editorSession}
        />
      }
    </>
  );
}

const ProgressiveGatingTrigger = ({ 
  id, 
  setState,
  editorSession,
  isProgressiveGatingEnabled, 
}) => {
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`, []));
  const gatingConfigurations = useSelector(state => _.get(state, 'storyboards.storyboard.item.gatingConfigurations', []));
  const formWidgets = widgets.filter((item) => item.type === widgetTypes.FORM);
  const externalFormsLength = widgets.filter((item) => item.type === widgetTypes.EXTERNAL_FORM)?.length;
  const formWidgetsLength = formWidgets.length;
  const isOtpEnabled = formWidgets.some((item) => _.get(item, 'properties.desktop.emailOtp', false));

  const isSmartGatingEnabled = useMemo(() => { 
    return !!gatingConfigurations.find(config => config?.gating?.id === id && config?.smart)?.enabled;
  }, [gatingConfigurations, id]);
  
  const progressive = useSelector(state => _.get(state, `gatings.gating.${id}.item.progressive`, false));
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`, 'desktop'));
  const gatingItems = useSelector(state => _.get(state, `gatings.items`, [])) || []
  const hasExistingProgressiveGating = !!gatingItems.filter(item => item.id !== id && item.progressive)?.length;
  const infoDisplayedRef = useRef(false);

  const allowProgressive = formWidgetsLength === 1 && externalFormsLength === 0;
  const hasNoForms = formWidgetsLength === 0;

  const dispatch = useDispatch();
  
  useEffect(() => {
    infoDisplayedRef.current = false;
  }, [id]);

  useEffect(() => {
    if(formWidgetsLength === 0 || (externalFormsLength && formWidgetsLength)) {
      dispatch(updateProgressiveGatingProperty(id, {
        progressive: false,
        progressiveGatingClusters: []
      }));
      setState((state) => ({...state, selected: null}));
    }
  }, [formWidgetsLength, externalFormsLength]);

  const progressiveRestriction = () => {
    const hasNoForms = formWidgetsLength === 0;

    let errorMessage = '';

    if(!allowProgressive) {
      errorMessage = 'Progressive Gating works with one form only. Remove the extra forms to continue.';
    }

    if(isSmartGatingEnabled) {
      errorMessage = 'Smart Gating is already active on this screen. Turn it off to use Progressive Gating.';
    }

    if(isOtpEnabled) {
      errorMessage = 'OTP is enabled on the existing form. Disable OTP to use Progressive Gating.';
    }

    if (hasNoForms) {
      errorMessage = 'Add a form to this screen to enable Progressive Gating.';
    }

    if(errorMessage) {
      toast(
        <FailureToast
          heading="Progressive Gating"
          description={errorMessage}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      )
      return false;
    }

    return true;
  };

  const gatingClusterHandler = () => {
    const isAllowed = progressiveRestriction();
    if(isAllowed) {
      setState((state) => ({...state, selected: state.selected === 'progressive-gating' ? null : 'progressive-gating'}));
    }
  }

  const toggleHandler = () => {
    const isAllowed = progressiveRestriction();
    if(isAllowed) {

      if(hasExistingProgressiveGating && !infoDisplayedRef.current && !progressive) {
        infoDisplayedRef.current = true;

        toast(
          <SuccessToast
            heading="Progressive Gating"
            description="Progressive gating will be disabled on other screens."
            info
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
      }

      dispatch(updateProgressiveGatingProperty(id, {
        progressive: !progressive,
      }));

      if(!progressive) {
        setState((state) => ({...state, selected: 'progressive-gating'}));
      } else {
        setState((state) => ({...state, selected: null}));
      }
    }
  };

  if(breakpoint === 'mobile' || hasNoForms) return null;

  return (
    <div className={styles.progressiveGatingTrigger}>
      <Form.Check
        type="switch"
        id="custom-switch"
        onChange={toggleHandler}
        checked={progressive}
        className={styles.toggleSwitch}
      />
      <span className={cx(styles.title, { [styles.enabled]: isProgressiveGatingEnabled })} onClick={gatingClusterHandler}>Progressive Gating</span>
    </div>
  )
}

const InfoCard = ({ item }) => {
  let { title, count, status, range, className, maxLimit} = item;
  return(
    <div className={cx(styles.infoCard, styles[className])}>
      <span className={styles.title}>{title}</span>
      <div className={styles.stats}>
        <span className={styles.numbers}>{count}</span>
        <span className={cx(styles.status, styles[status])}>{status}</span>
      </div>
      <Form.Range
        className={styles.sliderControl}
        min={0}
        max={maxLimit}
        value={parseFloat(count)}
      />
      <div className={styles.rangeGuide}>
        <i className={cx('cleverstory-help')} />
        <span className={styles.range}>{range}</span>
      </div>
    </div>
  )
}

const InteractiveDropdown = ({ editorSession, storyboard, entityType }) => {
  const dispatch = useDispatch();

  const { id } = useParams();

  const history = useHistory();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  var activePageId = urlParams.get('active');
  
  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const headerContext = React.useContext(HeaderContext);
  const type = _.get(headerContext, 'type', 'page');

  const pages = useSelector(state => _.get(state, 'storyboards.storyboard.pages.items', []));
  const gatings = useSelector(state => _.get(state, 'gatings.items', []));

  const activePage = useSelector((state) => {
    const active = _.get(state, `${editorSession}.present.interactive.active`);
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find((item) => item.uid === active);
  });

  const visible = _.get(activePage, 'properties.desktop.visible', true);
  const pageWidgets = useSelector(state => {
      const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
      var pages = widgets.filter(item => item.type === widgetTypes.PAGE);
      pages = _.sortBy(pages, 'order');
      return pages;
    });

  useEffect(() => {
    if (storyboard) {
      handleSearchPages();
    }
  }, [storyboard]);

  const handleChange = (page) => {
    history.push(`${baseUrl}/pages/${page.page}?active=${page.uid}`);
  };

  useEffect(() => {
    if (type === 'page' && activePage && activePage.uid !== activePageId) {
      history.push(`${baseUrl}/pages/${id}?active=${activePage.uid}`);
    }
  }, [activePage])

  useEffect(() => {
    if (type === 'page' && activePageId && activePageId !== _.get(activePage, 'uid', null)) {
      dispatch(switchInteractivePageSection(editorSession, activePageId));
    }
  }, [activePageId])

  const handleSearchPages = () => {
    var request = {
      storyboard: storyboard.id,
      draft: true
    }

    dispatch(fetchAllPagesForInteractiveStoryboard(request));
  }

  const _renderTitle = () => {
    if (type === 'page') {
      var activePage = pages.find(item => item.uid === activePageId);
      return _.get(activePage, 'name', '');
    } else {
      var activeGating = gatings.find(item => item.id === id);
      return _.get(activeGating, 'name', '');
    }
  }

  return (
    <Dropdown className={styles.pageDropDown}>
      <Dropdown.Toggle>
        <span className={styles.name}>
          <TruncatedTextWithTooltip placement="top" maxWidth={180} content={_renderTitle()}>
            {_renderTitle()}
          </TruncatedTextWithTooltip>
          {!visible &&
            <i className={cx('cleverstory-hide', styles.iconHide)}></i>
          }
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {pages.map((page) => {
          const active = page.uid === _.get(activePage, 'uid');
          const mappedPage = _.head(pageWidgets.filter((item) => item.uid === page.uid));
          let visible = _.get(mappedPage, 'properties.desktop.visible', true);
          if (entityType === 'GATING'){
            visible = _.get(page, 'visible');
          }
          return (
            <Dropdown.Item active={active} key={page.uid} onClick={() => handleChange(page)}>
              <span className={styles.name}>
                <TruncatedTextWithTooltip placement="top" maxWidth={180} content={page.name}>
                  {page.name}
                </TruncatedTextWithTooltip>
                {!visible &&
                  <i className={cx('cleverstory-hide', styles.iconHide)}></i>
                }
              </span>
            </Dropdown.Item>
          );
        })}

        <GatingDropdown />
      </Dropdown.Menu>
    </Dropdown>
  );
};

const LandingPageDropdown = ({ storyboard }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const { id } = useParams();

  const headerContext = React.useContext(HeaderContext);
  const type = _.get(headerContext, 'type', 'page');

  const pages = useSelector(state => _.get(state, 'pages.items', []));
  const loading = useSelector(state => _.get(state, 'pages.loading', false));
  const fetched = useSelector(state => _.get(state, 'pages.fetched', false));
  const gatings = useSelector(state => _.get(state, 'gatings.items', []));

  const currentPage = pages.find(page => page.id === id)
  const hidden = _.get(currentPage, 'hidden', false)

  const homePage = _.get(storyboard, 'page', null);

  const isHomePage = (id) => {
    return homePage && homePage.id === id;
  }

  useEffect(() => {
    if (storyboard?.id && !fetched) {
      handleSearchPages();
    }
  }, [storyboard?.id]);

  const handleSearchPages = () => {
    var request = {
      storyboard: storyboard.id
    }

    dispatch(searchPages(request, 0, 100));
  }

  const handleChange = (page) => {
    if (id === page.id){
      return;
    }
    dispatch(invalidatePageChange(page.id));
    history.push(`${baseUrl}/pages/${page.id}`);
  }

  const _renderTitle = () => {
    if (type === 'page') {
      var activePage = pages.find(item => item.id === id);
      return _.get(activePage, 'name', '');
    } else {
      var activeGating = gatings.find(item => item.id === id);
      return _.get(activeGating, 'name', '');
    }
  }

  const handleToggle = (isOpen) => {
    if (
      isOpen &&
      storyboard?.id && 
      [...SEGMENT_STORYBOARDS, STORYBOARD_TYPES.SPATIAL].includes(storyboard?.type)
    ) {
      dispatch(revalidatePagesList({ storyboard: storyboard.id }, 0, 100));
    }
  };

  return (
    <Dropdown className={styles.pageDropDown} onToggle={handleToggle}>
      <Dropdown.Toggle>
        <span className={styles.name}>
          {(isHomePage(id) && !MULTIPAGE_STORYBOARDS.includes(storyboard.type)) && <i className={cx('cleverstory-home', styles.homeIcon)}></i>}
          <TruncatedTextWithTooltip placement="top" maxWidth={180} content={_renderTitle()} >
            {_renderTitle()}
          </TruncatedTextWithTooltip >
          {hidden && <i className={cx('cleverstory-hide', styles.iconHide)}></i>}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {pages && pages.map((page) => {
          const active = page.id === id;
          return (
            <Dropdown.Item active={active} key={page.id} onClick={() => handleChange(page)}>
              <span className={styles.name}>
                {(isHomePage(page.id) && !MULTIPAGE_STORYBOARDS.includes(storyboard.type) && <i className={cx('cleverstory-home', styles.homeIcon)}></i>)}
                <TruncatedTextWithTooltip placement="top" content={page.name}>
                  {page.name}
                </TruncatedTextWithTooltip>
                {page.hidden && <i className={cx('cleverstory-hide', styles.iconHide)}></i>}
              </span>
            </Dropdown.Item>
          );
        })}

        <GatingDropdown />
      </Dropdown.Menu>
    </Dropdown>
  );
}

const GatingDropdown = () => {
  const history = useHistory();

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const gatings = useSelector(state => _.get(state, 'gatings.items', []));

  const handleGatingClick = (gating) => {
    history.push(`${baseUrl}/gating/${gating.id}`);
  }

  if (!gatings || gatings.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown.Divider className={styles.dropDownDivider} />
      <Dropdown.Header>GATING FORMS</Dropdown.Header>
      {gatings.map((data, index) => {
        return (
          <Dropdown.Item key={index} onClick={() => handleGatingClick(data)}>
            <span className={styles.name}>
              <TruncatedTextWithTooltip placement="top" content={data.name}>
                {data.name}
              </TruncatedTextWithTooltip>
            </span>
          </Dropdown.Item>
        )
      })
      }
    </>
  )
}

export const PageAction = ({ editorSession, selected, onClose, storyboard, target, disabled, entityType }) => {
  const dispatch = useDispatch();
  const [offset, setOffset] = useState(55);
  const storyboardType = _.get(storyboard, 'type', '');

  const activePageSection = useSelector((state) => {
    if((SEGMENT_STORYBOARDS.includes(storyboardType)) && entityType !== "GATING") {
      const selectedPage = _.get(state, `${editorSession}.present.selectedPage`);
      const activeSegment = _.get(state, `${editorSession}.present.activeSegment`);

      if (selectedPage && activeSegment && selectedPage !== activeSegment) {
        return activeSegment;
      }

      return selectedPage || activeSegment;
    }
    return _.get(state, `${editorSession}.present.interactive.active`);
  });
  const colorPaletteId = useSelector(state => {
    const brandkitItems = _.get(state, 'palette.brandkit.items', []);
    return brandkitItems.find((brandkitItem) => brandkitItem.type === 'COLORS')?.id || "";
  });
  const brandkitEnabled = useSelector(state => _.get(state, 'workspaces.workspace.item.brandkitEnabled', false));

  const headerContext = React.useContext(HeaderContext);
  const type = _.get(headerContext, 'type', 'page');


  useLayoutEffect(() => {
    if(selected){
      const selectedButton = document.getElementById(`button-palette`);
      if (selectedButton) {
        const rect = selectedButton.getBoundingClientRect();
        const offsetValue = rect.y + rect.height;
        setOffset(offsetValue);
      }
    }
  }, [selected]);

  useEffect(() => {
    dispatch(fetchPalettesForWorkspace());
    dispatch(fetchPalettes())
  }, [])

  useEffect(() => {
    if(!storyboard?.id) return;

    dispatch(fetchStoryboard(storyboard.id));
  }, [storyboard?.id]);

  // Stale-while-revalidate: silently refresh pages list when Page settings opens (syncs deleted pages from other users)
  useEffect(() => {
    if (selected === 'settings' && storyboard?.id && [...SEGMENT_STORYBOARDS, STORYBOARD_TYPES.SPATIAL].includes(storyboardType)) {
      dispatch(revalidatePagesList({ storyboard: storyboard.id }, 0, 100));
    }
  }, [selected, storyboard?.id, storyboardType]);

  useEffect(() => {
    if (colorPaletteId && brandkitEnabled) {
      handleComponentsForPalette(0);
    }
  }, [colorPaletteId, brandkitEnabled]);

  const handleComponentsForPalette = (page) => {
    var request = {
      sort: 'order',
      order: 'ASC'
    }

    dispatch(fetchComponentsForPalette(colorPaletteId, "brandkit", request, page, 50));
  }
  
  const renderPageHeader = () => {
    switch (selected) {
      case 'palette': {
        return 'Add element to Storyboard';
      }

      case 'settings': {
        return 'Page Settings'
      }

      case 'gatingSettings': {
        return 'Gating Setup'
      }

      case 'page-details': {
        return 'Page Details'
      }

      default: {
        return null;
      }
    }
  }

  return (
    <Offcanvas
      className={styles.offcanvas}
      show={!disabled && selected !== null && selected !== 'progressive-gating'}
      backdrop={false}
      keyboard={true}
      onHide={onClose}
      style={{
        top: offset,
      }}
    >
      <Offcanvas.Header >
        <Offcanvas.Title>{renderPageHeader()}</Offcanvas.Title>
        <Button className="close" variant="info" onClick={onClose}>
          <i className={cx(styles.closeIcon, "cleverstory-close")} />
        </Button>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <TabContainer id="page_action_tabs" activeKey={selected}>
          <Tab.Content>
            <Tab.Pane eventKey="palette">
              <Palette
                editorSession={editorSession}
                defaultParent={activePageSection}
                open={selected !== null}
                style={{
                  top: offset,
                }}
                onClose={onClose}
                storyboardType={storyboardType}
                editorType={type.toUpperCase()}
                target={target}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="settings">
              {storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT &&
                <PageSettings
                  editorSession={editorSession}
                  defaultParent={activePageSection}
                  onClose={onClose}
                  style={{
                    top: offset
                  }}
                  type={type}
                />
              }
              {storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2 &&
                <IcV2PageSettings
                  editorSession={editorSession}
                  defaultParent={activePageSection}
                  onClose={onClose}
                  type={type}
                />
              }
              {!INTERACTIVE_CONTENT_TYPES.includes(storyboardType) &&
                <LangingPageSettings
                  editorSession={editorSession}
                  defaultParent={activePageSection}
                  onClose={onClose}
                  style={{
                    top: offset
                  }} 
                  type={type}
                  />
              }

            </Tab.Pane>
            <Tab.Pane eventKey="gatingSettings">
              <GatingSettingsPage
                editorSession={editorSession}
                defaultParent={activePageSection}
                open={selected !== null}
                style={{
                  top: offset,
                }}
                onClose={onClose}
                storyboardType={storyboardType}
                editorType={type.toUpperCase()}
                target={target}
              />
            </Tab.Pane>
          </Tab.Content>
        </TabContainer>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

const PublishMessageModal = ({ show, onDiscard, pageData }) => {

  return (
    <Modal backdrop='static'
      className={styles.publishErrorModal}
      contentClassName={styles.publishErrorContent}
      dialogClassName={styles.publishErrorDialog}
      backdropClassName={styles.backdropWrapper}
      show={show}
      centered size="lg"
    >
      <Modal.Body className={styles.publishErrorBody}>
        <Image src={'/images/saveChanges.png'} className={styles.saveMessageImage} />
        <Row className={styles.messageRow}>
          <Col className={styles.messageContainer} xs={{ span: 5, offset: 7 }}>
            <div>
              <h2 className={styles.messageTitle}>Wait, what?</h2>
              <span className={styles.messageContent}>
                Changes made to this mission<br /> haven’t been published.<br />
              </span>
            </div>
            <div className={styles.aliasList}>
              {pageData &&
                pageData.map((value) => (
                  <div id={value.id} key={value.id} className={styles.pageInfo}>
                    <span className={styles.pageName}>{value.id}</span>
                    <span className={styles.pageAlias}>{value.alias}</span>
                  </div>
                ))
              }
            </div>
            <footer className={styles.publishErrorButtons}>
              <Button variant="secondary" onClick={() => onDiscard()} >Close</Button>
            </footer>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export const PaletteButton = ({ onButtonSelection, selected, disabled }) => {
  return (
    <Button
      id="button-palette"
      className={cx(styles.action, styles.palette, {
        [styles.open]: selected === "palette",
      })}
      disabled={disabled}
      onClick={onButtonSelection.bind(this, "palette")}
    >
      <i className={cx("cleverstory-plus")} />
    </Button>
  )
}

const PageSettingsButton = ({ onButtonSelection, disabled }) => {

  return (
    <Tooltip placement='bottom' content='Page Settings'>
      <Button
        id='gating-button-settings' 
        variant="info" 
        className={cx(styles.pageSettings)} 
        disabled={disabled}
        onClick={onButtonSelection.bind(this, 'settings')}
      >
        <i className={cx("cleverstory-page-settings")} />
      </Button>
    </Tooltip>
  )
}


const GatingSettingsButton = ({ onButtonSelection, disabled, storyboardType }) => {
  const gatingConfigurations = useSelector(state => _.get(state, 'storyboards.storyboard.item.gatingConfigurations', []));

  const isSmartGatingEnabled = useMemo(() => { 
    return !!gatingConfigurations.find(config => config.smart)?.enabled;
  }, [gatingConfigurations]);

  return (
    <Tooltip placement='bottom' content='Gating Setup'>
      <Button
        id='gating-button-settings' 
        variant="info" 
        className={cx(styles.pageSettings, styles.gatingSettings)} 
        disabled={disabled}
        onClick={() => onButtonSelection('gatingSettings')}
        data-track-event="Mixpanel" data-track-props="Gating Setup from Editor"
      >
        {isSmartGatingEnabled &&
          <Image
            className={styles.pulseImage}
            src={smartGatingIcon}
            width={22}
            height={22}
          />
        }
          <i className={cx("cleverstory-gating-settings")} />
      </Button>
    </Tooltip>
  )
}

export const ToolsButton = ({ tools, disabled, onToggle }) => {
  return (
    <Tooltip placement='bottom' content='Tools'>
      <Button
        id='tools' className={styles.actionButton} 
        variant="info" 
        active={_.get(tools, 'toolbar')} 
        disabled={disabled}
        onClick={() => onToggle("toolbar")}
      >
        <i className={cx("cleverstory-tool-bar")} />
      </Button>
    </Tooltip>
  )
}

export const LayersButton = ({ tools, onToggle, disabled }) => {
  return (
    <Tooltip placement='bottom' content='Layers'>
      <Button
        id="layers" 
        className={styles.actionButton} 
        variant="info" 
        active={_.get(tools, 'layers')} 
        disabled={disabled}
        onClick={() => onToggle("layers")}
      >
        <i className={cx("cleverstory-layers")} />
      </Button>
    </Tooltip>
  )
}


export const VariablesButton = ({ tools, onToggle, disabled }) => {
  return (
    <Tooltip placement='bottom' content='Variables'>
      <Button
        id="variables" 
        className={styles.actionButton} 
        variant="info" 
        active={_.get(tools, 'variables')} 
        disabled={disabled}
        data-track-event="Mixpanel" data-track-props="Variable Palette" 
        onClick={() => onToggle("variables")}
      >
        <i className={"cleverstory-variable"} />
      </Button>
    </Tooltip>
  )
}

export const ResourceCenterButton = ({ openContentManager, disabled}) => {
  return (
    <Tooltip placement='bottom' content='Content Manager'>
      <Button
        id="layers" 
        className={styles.actionButton} 
        variant="info" 
        disabled={disabled}
        onClick={() => openContentManager()}
      >
        <i className={cx("cleverstory-content-manager")} />
      </Button>
    </Tooltip>
  )
}

export const SlidePageSettingAction = ({ handleSpatialBackground, disabled }) => {
  return (

    <Tooltip placement='bottom' content='PageDetails'>
    <Button
      id="layers" 
      disabled={disabled}
      className={styles.actionButton} 
      variant="info" 
      onClick={() => handleSpatialBackground('pageDetails')}
    >
      <i className={cx("cleverstory-slider-menu")} />
    </Button>
  </Tooltip>
  );
};

export const UndoButton = ({ canUndo, onUndo, disabled }) => {
  return (
    <Tooltip placement="bottom" content="Undo">
      <Button
        disabled={disabled || !canUndo}
        className={styles.actionButton}
        variant="info"
        onClick={onUndo}
      >
        <i className={cx("cleverstory-undo")} />
      </Button>
    </Tooltip>
  )
}

export const RedoButton = ({ canRedo, onRedo, disabled }) => {
  return (
    <Tooltip placement="bottom" content="Redo">
      <Button
        disabled={disabled || !canRedo}
        variant="info"
        className={styles.actionButton}
        onClick={onRedo}
      >
        <i className={cx("cleverstory-redo")} />
      </Button>
    </Tooltip>
  )
}

export const OptimizeLayoutButton = ({ onOptimizeLayout }) => {
  return (
    <Button
      className={cx('small', styles.optimizeLayout)}
      variant="primary" onClick={onOptimizeLayout}>
      Optimize Layout
    </Button>
  )
}

export const DesktopButton = ({ breakpoint, onSwitchBreakpoint }) => {
  return (
    <Tooltip placement="bottom" content="Desktop">
      <Button
        variant="info"
        active={breakpoint === "desktop"}
        className={styles.actionButton}
        onClick={() => onSwitchBreakpoint("desktop")}
      >
        <i className={cx("cleverstory-laptop")} />
      </Button>
    </Tooltip>
  )
}

export const MobileButton = ({ breakpoint, onSwitchBreakpoint, layoutChangeIndicator = false}) => {

  return (
    <Tooltip placement="bottom" className={cx({ [styles.mobileTooltip]: layoutChangeIndicator })} content={layoutChangeIndicator ? 'There are changes in the mobile view that you may have not noticed.' : 'Mobile'}>
      <Button
        variant="info"
        active={breakpoint === "mobile"}
        className={cx(styles.actionButton, {[styles.mobileViewButton]: layoutChangeIndicator})}
        onClick={() => onSwitchBreakpoint("mobile")}
      >
        <i className={cx("cleverstory-mobile")} />
      </Button>
    </Tooltip>
  )
}

export const RulerButton = ({ tools, onToggle, disabled }) => {
  return (
    <Tooltip placement="bottom" content="Ruler">
      <Button
        variant="info"
        active={_.get(tools, 'rulers')}
        className={styles.actionButton}
        disabled={disabled}
        onClick={() => onToggle("rulers")}
      >
        <i className={cx("cleverstory-scale")} />
      </Button>
    </Tooltip>
  )
}

export const GridlinesButton = ({ tools, disabled, onToggle }) => {
  return (
    <Tooltip placement="bottom" content="Grid">
      <Button
        variant="info"
        active={_.get(tools, 'gridlines')}
        disabled={disabled}
        className={styles.actionButton}
        onClick={() => onToggle("gridlines")}
      >
        <i className={cx("cleverstory-grids")} />
      </Button>
    </Tooltip>
  )
}

const LastUpdatedDate = ({value}) => {
  const [date, setDate] = useState(null);
  const timerRef = React.useRef(null);

  const calculateDate = () => {
    const dateValue = moment(new Date(value)).fromNow();
    setDate(dateValue);
  }

  useEffect(() => {
    calculateDate();

    timerRef.current = setInterval(() => {
      calculateDate();
    }, 10000);

    return () => {
      if(timerRef.current){
        clearInterval(timerRef.current);
      }
    }
  }, [value]);

  if(!date){
    return "";
  }

  return date;
}